// @flow
import React, { PureComponent } from 'react';
import type { Node } from 'react';
import PropTypes from 'prop-types';

import CheckBoxGroup from '../CheckBoxGroup';
import Input from 'Common/Input';

// Styles
import bootstrap from 'vendor/bootstrap/_bootstrap_custom.scss';

// Helpers
import classNames from 'classnames';
import { getKey } from 'Common/formValues';

import { withinDistanceValues } from 'Common/filterValues';

import {
  getPriceFromValues,
  getPriceToValues,
  getPriceLabelsAndFields
} from 'Common/calculatePriceIncrements';

/**
 * <BasicFiltersResults /> component.
 * @return {class} returns BasicFiltersMultiSelect instance
 */
class BasicFiltersResults extends PureComponent<*, *> {
  timeouts: any[];
  /**
   * @param {object} props - props passed
   */
  constructor(props: Object) {
    super(props);
    this.timeouts = [];
  }

  /**
   * componentDidMount
   * @return {void}
   */
  componentDidMount() {
    // $FlowFixMe
    document.addEventListener('click', this.props.closeSwoosh);
  }

  /**
   * componentWillUnmount
   * @return {void}
   */
  componentWillUnmount() {
    // $FlowFixMe
    document.removeEventListener('click', this.props.closeSwoosh);
    this.timeouts.forEach(clearTimeout);
  }

  /**
   * Implements propTypes().
   *
   * @return {Object} - propType object.
   * @prop {object} styles - styles.
   * @prop {array} makes - makes.
   * @prop {object} fields - fields.
   * @prop {array} models - models.
   * @prop {number} priceFrom - priceFrom.
   * @prop {number} priceTo - priceTo.
   * @prop {function} checkPostcode - checkPostcode.
   * @prop {boolean} isPostcodeValid - isPostcodeValid.
   */
  static propTypes = {
    styles: PropTypes.object.isRequired,
    makes: PropTypes.array.isRequired,
    models: PropTypes.array,
    priceFrom: PropTypes.number,
    priceTo: PropTypes.number,
    fields: PropTypes.object,
    resetField: PropTypes.func,
    checkPostcode: PropTypes.func,
    isPostcodeValid: PropTypes.bool,
    isResultsLoading: PropTypes.bool,
    isStockCarsPage: PropTypes.bool,
    trims: PropTypes.array
  };

  /**
   * Renders <BasicFilters /> component.
   *
   * @return {JSX} - renders a logo.
   */
  render(): Node {
    const {
      styles,
      makes,
      models,
      trims,
      fields,
      priceFrom,
      priceTo,
      resetField,
      checkPostcode,
      isPostcodeValid,
      isResultsLoading,
      highlight,
      setPostcodeRef,
      isMobile,
      setAsSelected,
      currentSelection,
      isLeasingPage,
      isStockCarsPage
    } = this.props;

    const {
      priceFromLabel,
      priceToLabel,
      fieldsMinPrice,
      fieldsMaxPrice,
      minPriceOrMinLeasePrice,
      maxPriceOrMaxLeasePrice
    } = getPriceLabelsAndFields(isLeasingPage, fields);

    return (
      <div>
        <div className={classNames(styles.basicFormContainer)}>
          <CheckBoxGroup
            styles={styles}
            name={'make'}
            label={'Select make'}
            selectedValue={fields.make}
            options={makes}
            isSelected={currentSelection === 'make'}
            onSelect={() => setAsSelected('make')}
            setAsSelected={() => setAsSelected(null)}
            onClose={() => setAsSelected(null)}
            isSwoosh={true}
            resetField={() => resetField('make')}
            isDisabled={isResultsLoading}
            isMobile={isMobile}
          />

          <CheckBoxGroup
            styles={styles}
            name={'model'}
            label={'Select model'}
            selectedValue={fields.model}
            options={models}
            isSelected={currentSelection === 'model'}
            onSelect={() => setAsSelected('model')}
            onClose={() => setAsSelected(null)}
            setAsSelected={setAsSelected}
            isSwoosh={true}
            resetField={() => resetField('model')}
            isDisabled={!fields.make || isResultsLoading}
            isMobile={isMobile}
          />

          <CheckBoxGroup
            styles={styles}
            name={'trim'}
            label={'Trims'}
            selectedValue={fields.trim}
            options={trims}
            isSelected={currentSelection === 'trim'}
            onSelect={() => setAsSelected('trim')}
            onClose={() => setAsSelected(null)}
            setAsSelected={setAsSelected}
            resetField={() => resetField('trim')}
            disableFiltering={true}
            isMobile={isMobile}
          />

          <CheckBoxGroup
            styles={styles}
            name={minPriceOrMinLeasePrice}
            label={priceFromLabel}
            selectedValue={fieldsMinPrice}
            options={getPriceFromValues(
              priceFrom,
              priceTo,
              getKey(fieldsMaxPrice),
              isLeasingPage,
              isStockCarsPage
            )}
            disableCounts={true}
            isSelected={currentSelection === minPriceOrMinLeasePrice}
            onSelect={() => setAsSelected(minPriceOrMinLeasePrice)}
            onClose={() => setAsSelected(null)}
            setAsSelected={setAsSelected}
            resetField={() => resetField(minPriceOrMinLeasePrice)}
            isSwoosh={true}
            isDisabled={isResultsLoading}
            isMobile={isMobile}
          />
          <CheckBoxGroup
            styles={styles}
            name={maxPriceOrMaxLeasePrice}
            label={priceToLabel}
            selectedValue={fieldsMaxPrice}
            options={getPriceToValues(
              priceFrom,
              priceTo,
              getKey(fieldsMinPrice),
              isLeasingPage,
              isStockCarsPage
            )}
            disableCounts={true}
            isSelected={currentSelection === maxPriceOrMaxLeasePrice}
            onSelect={() => setAsSelected(maxPriceOrMaxLeasePrice)}
            onClose={() => setAsSelected(null)}
            setAsSelected={setAsSelected}
            resetField={() => resetField(maxPriceOrMaxLeasePrice)}
            isSwoosh={true}
            isDisabled={isResultsLoading}
            isMobile={isMobile}
          />
          {!isLeasingPage && (
            <div className={styles.inputContainer}>
              <Input
                styles={styles}
                bootstrap={bootstrap}
                name={'postCode'}
                label={'Postcode'}
                labelIcon={'location'}
                highlight={highlight}
                resetField={() => resetField('postCode')}
                onChangeHandler={checkPostcode}
                selectedValue={fields.postCode}
                isLabelHidden={true}
                placeholderText={'Enter here'}
                isValid={isPostcodeValid}
                errorMessage={'Postcode invalid'}
                setRef={setPostcodeRef}
              />
            </div>
          )}
          {!isLeasingPage && isPostcodeValid && (
            <CheckBoxGroup
              styles={styles}
              name={'withinDistance'}
              label={'Distance'}
              selectedValue={fields.withinDistance}
              options={withinDistanceValues}
              disableCounts={true}
              isSelected={currentSelection === 'withinDistance'}
              onSelect={() => setAsSelected('withinDistance')}
              onClose={() => setAsSelected(null)}
              setAsSelected={setAsSelected}
              disableFiltering={true}
              resetField={() => resetField('withinDistance')}
              isMobile={isMobile}
            />
          )}
        </div>
      </div>
    );
  }
}

export default BasicFiltersResults;



// WEBPACK FOOTER //
// ./src/Common/BasicFiltersResults/index.js